import { useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { FlexibleTracking } from './FlexibleTracking';
import { GeneralList2PriceMismatch } from './GeneralList2PriceMismatch';
import { supplyService } from '../services/supplyService';

export function TrackingListPage() {
  const [includeLocked, setIncludeLocked] = useState(false);
  const [backfillingLkm, setBackfillingLkm] = useState(false);
  const [backfillingDuplicateStops, setBackfillingDuplicateStops] = useState(false);
  const [backfillingFinancialPrices, setBackfillingFinancialPrices] = useState(false);
  const [financialShipmentNo, setFinancialShipmentNo] = useState('');
  const [activeTab, setActiveTab] = useState<'tracking' | 'price-mismatch'>('tracking');

  const handleBackfillLkm = async () => {
    try {
      setBackfillingLkm(true);
      const result: any = await supplyService.backfillLkm();
      toast.success(result?.message || 'LKM backfill tamamlandı');
    } catch (error: any) {
      toast.error(error?.message || 'LKM backfill başarısız');
    } finally {
      setBackfillingLkm(false);
    }
  };

  const handleBackfillDuplicateStops = async () => {
    try {
      setBackfillingDuplicateStops(true);
      const result: any = await supplyService.backfillRemoveDuplicateStops();
      toast.success(result?.message || 'Çakışan nokta backfill tamamlandı');
    } catch (error: any) {
      toast.error(error?.message || 'Çakışan nokta backfill başarısız');
    } finally {
      setBackfillingDuplicateStops(false);
    }
  };

  const handleBackfillFinancialPrices = async () => {
    try {
      setBackfillingFinancialPrices(true);
      const result: any = await supplyService.backfillRouteBasedFinancialPrices(25, 0, financialShipmentNo);
      const data = result?.data || {};
      const remainingText = !financialShipmentNo.trim() && data.hasMore ? ' Kalan kayıtlar için işlem bitince tekrar basın.' : '';
      toast.success(`${Number(data.processed || 0)} kayıt tarandı, ${Number(data.updated || 0)} kaydın rota bazlı fiyatı düzeltildi.${remainingText}`);
    } catch (error: any) {
      toast.error(error?.message || 'Rota bazlı fiyat backfill başarısız');
    } finally {
      setBackfillingFinancialPrices(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-white px-4 py-1">
        <button type="button" onClick={() => setActiveTab('tracking')} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${activeTab === 'tracking' ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>
          Genel Liste
        </button>
        <button type="button" onClick={() => setActiveTab('price-mismatch')} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${activeTab === 'price-mismatch' ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>
          Uyumsuz Fiyatlar
        </button>
        {activeTab === 'tracking' ? (
          <button
            type="button"
            onClick={() => void handleBackfillLkm()}
            disabled={backfillingLkm}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50"
          >
            {backfillingLkm ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {backfillingLkm ? 'LKM Güncelleniyor...' : 'LKM Backfill'}
          </button>
        ) : null}
        {activeTab === 'tracking' ? (
          <>
            <input
              value={financialShipmentNo}
              onChange={(event) => setFinancialShipmentNo(event.target.value)}
              placeholder="Sevk kodu (opsiyonel)"
              className="h-8 w-40 rounded-lg border border-violet-200 bg-violet-50 px-2 text-xs font-medium text-violet-900 outline-none placeholder:text-violet-400 focus:border-violet-400"
            />
            <button
              type="button"
              onClick={() => void handleBackfillFinancialPrices()}
              disabled={backfillingFinancialPrices}
              className="inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50"
              title="Minimum fiyatla açılmış siparişleri rota baz fiyatına göre düzeltir"
            >
              {backfillingFinancialPrices ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {backfillingFinancialPrices ? 'Fiyat Düzeltiliyor...' : 'Rota Bazlı Fiyatları Düzelt'}
            </button>
          </>
        ) : null}
        {activeTab === 'tracking' ? (
          <button
            type="button"
            onClick={() => void handleBackfillDuplicateStops()}
            disabled={backfillingDuplicateStops}
            className="inline-flex items-center gap-1.5 rounded-lg border border-sky-300 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-50"
            title="Ana teslim/yükleme noktası ile aynı olan ara noktaları otomatik kaldırır"
          >
            {backfillingDuplicateStops ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {backfillingDuplicateStops ? 'Çakışma Gideriliyor...' : 'Çakışan Noktaları Temizle'}
          </button>
        ) : null}
        <label className="ml-2 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700">
          <input
            type="checkbox"
            checked={includeLocked}
            onChange={(event) => setIncludeLocked(event.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
          />
          Kilitli kayıtları getir
        </label>
      </div>
      <div className="min-h-0 flex-1">
        {activeTab === 'tracking' ? (
          <FlexibleTracking
            key={includeLocked ? 'tracking-list-with-locked' : 'tracking-list-without-locked'}
            dataService={supplyService}
            dataParams={{ includeLocked }}
            settingsKeyScope="flexible_tracking"
            title="Operasyon Takip"
            subtitle="Filtre ve gruplamalar kullanıcı bazlı hızlı arama tabı olarak kaydedilir."
            defaultVisibleColumns={['sevkKodu', 'yuklemeMusterisi', 'bosaltmaMusterisi', 'teslimNoktasiSayisi', 'ugrmaSayisi', 'bazFiyat', 'hedefFiyat', 'durakBedeli', 'navlun', 'durum', 'islemDurum']}
          />
        ) : (
          <GeneralList2PriceMismatch includeLocked={includeLocked} />
        )}
      </div>
    </div>
  );
}
